import { isDefined } from "@clipboard-health/util-ts";
import {
  PLACEMENT_COHORT,
  type PlacementCandidate,
  PlacementCandidateScreeningStatus,
} from "@src/appV2/redesign/PlacementCandidate/types";
import { startCase } from "lodash";

import { type PlacementWithDetails } from "../../../api/useFetchPaginatedPlacements";
import { useInterviewOnJobCards } from "../../../hooks/useInterviewOnJobCards";
import { ListHelper } from "./ListHelper";
import { PlacementSectionTemplate } from "./PlacementSectionTemplate";
import { TalkToRecruiterInterviewAvailabilityBullet } from "./TalkToRecruiterInterviewAvailabilityBullet";

interface InterviewAvailabilitySectionProps {
  placement: PlacementWithDetails;
  placementCandidate: PlacementCandidate;
}

export function InterviewAvailabilitySection(props: InterviewAvailabilitySectionProps) {
  const { placement, placementCandidate } = props;

  const { allowRequestInterview, interviewSettings, isRecruiterScreeningEnabled } = placement;
  const { cohorts, enabled: interviewsOnJobCardsEnabled } = useInterviewOnJobCards();
  const isInterviewsOnJobCardsCohort =
    interviewsOnJobCardsEnabled && cohorts.includes(PLACEMENT_COHORT.INTERVIEWS_ON_JOB_CARDS);
  const interviewAvailability = interviewSettings?.attributes.availability;

  const availableDays = isDefined(interviewAvailability)
    ? getAvailableDays(interviewAvailability)
    : [];
  const formattedAvailableDays = formatAvailabilityStrings(availableDays);

  return (
    <PlacementSectionTemplate title="Interview availability" iconType="bolt">
      <ListHelper>
        {availableDays.length > 0 && (
          <ListHelper.Item>
            <ListHelper.Text>{`Taking interviews on ${formattedAvailableDays}`}</ListHelper.Text>
          </ListHelper.Item>
        )}
        {allowRequestInterview && (
          <ListHelper.Item>
            <ListHelper.Text>
              Provide your availability dates for an in-person interview and the workplace will
              reach out to you within 3 business days
            </ListHelper.Text>
          </ListHelper.Item>
        )}
        {isRecruiterScreeningEnabled &&
          !isInterviewsOnJobCardsCohort &&
          (placementCandidate.screeningStatus === PlacementCandidateScreeningStatus.NO_SCREENING ||
            placementCandidate.screeningStatus === PlacementCandidateScreeningStatus.FAILED) && (
            <TalkToRecruiterInterviewAvailabilityBullet
              placement={placement}
              placementCandidate={placementCandidate}
            />
          )}
      </ListHelper>
    </PlacementSectionTemplate>
  );
}

function getAvailableDays(availability: Array<{ dayOfWeek: string }>): string[] {
  const allAvailabilityStrings = availability.map((avail) => {
    const day = avail.dayOfWeek.toLowerCase();
    return startCase(day);
  });
  return [...new Set(allAvailabilityStrings)];
}

function formatAvailabilityStrings(days: string[]): string {
  const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const sortedDays = days.sort((a, b) => {
    const aIndex = DAY_ORDER.indexOf(a);
    const bIndex = DAY_ORDER.indexOf(b);
    return aIndex - bIndex;
  });

  if (sortedDays.length === 1) {
    return days[0];
  }

  const lastDay = sortedDays.pop()!;
  return `${sortedDays.join(", ")} & ${lastDay}`;
}

export function hasInterviewAvailabilityEnabled(placement?: PlacementWithDetails): boolean {
  if (!placement) {
    return false;
  }

  const { allowRequestInterview, interviewSettings } = placement;
  const hasAvailability =
    Boolean(interviewSettings?.attributes.scheduleInterviews) &&
    isDefined(interviewSettings?.attributes.availability) &&
    interviewSettings.attributes.availability.length > 0;

  return hasAvailability || (allowRequestInterview ?? false);
}
